/**
 * Reader input component for DEX writer workflow.
 * Searchable combobox for reader selection with validation.
 */

import { getReaderPublicKey } from '../../api/payloads.js';

/**
 * Render the reader input combobox into a container.
 * @param {HTMLElement} container - Container element
 * @param {Object} state - Component state
 * @param {string} [state.readerId] - Current reader ID value
 * @param {boolean} [state.validated] - Whether reader has been validated
 * @param {boolean} [state.loading] - Whether validation is in progress
 * @param {string|null} [state.error] - Error message
 * @param {Array<{id: string, publicKey: string}>} [state.recentReaders] - Recent readers list
 * @param {boolean} [state.disabled] - Whether input is disabled
 */
export function renderReaderInput(container, state = {}) {
  const {
    readerId = '',
    validated = false,
    loading = false,
    error = null,
    recentReaders = [],
    disabled = false
  } = state;

  // Determine border color based on state
  let borderClass = 'border-gray-300';
  if (error) {
    borderClass = 'border-red-500';
  } else if (validated) {
    borderClass = 'border-green-500';
  }

  // Placeholder text varies based on whether we have recent readers
  const placeholderText = recentReaders.length > 0
    ? 'Type to search or select reader...'
    : 'Enter reader ID...';

  container.innerHTML = `
    <div>
      <label for="reader-id" class="block text-sm font-medium text-gray-700 mb-1">
        Reader ID <span class="text-red-500">*</span>
      </label>

      <div class="relative">
        <input
          type="text"
          id="reader-id"
          list="recent-readers-list"
          autocomplete="off"
          class="w-full px-3 py-2 pr-10 border rounded-md ${borderClass} focus:ring-blue-500 focus:border-blue-500"
          placeholder="${placeholderText}"
          value="${escapeHtml(readerId)}"
          ${disabled || loading ? 'disabled' : ''}
        />
        <datalist id="recent-readers-list">
          ${recentReaders.map(r => `<option value="${escapeHtml(r.id)}">${escapeHtml(r.id)}</option>`).join('')}
        </datalist>
        <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          ${loading ? renderSpinnerIcon() : ''}
          ${validated && !loading ? renderCheckIcon() : ''}
        </div>
      </div>
      ${error ? `<p class="text-red-500 text-xs mt-1">${escapeHtml(error)}</p>` : ''}
      ${validated && !error ? `<p class="text-green-600 text-xs mt-1">Reader verified</p>` : ''}
    </div>
  `;
}

/**
 * Initialize reader input with event handlers.
 * @param {HTMLElement} container - Container element
 * @param {Object} callbacks - Callback functions
 * @param {Function} callbacks.onValidate - Called to validate: (readerId) => void
 * @param {Function} callbacks.onSelect - Called when selecting known reader: (readerId, publicKey) => void
 * @param {Function} callbacks.onClear - Called when input cleared: () => void
 * @param {Function} callbacks.onAutoSelect - Called when single reader auto-selected: (readerId, publicKey) => void
 * @param {Object} [state] - Current state (for auto-select logic)
 * @returns {Function} Cleanup function that removes event listeners
 */
export function initReaderInput(container, callbacks = {}, state = {}) {
  const { onValidate, onSelect, onClear, onAutoSelect } = callbacks;
  const {
    recentReaders = [],
    validated = false,
    loading = false,
    readerId = ''
  } = state;

  const cleanupFunctions = [];

  // Auto-select single reader if conditions are met
  if (recentReaders.length === 1 && !validated && !loading && !readerId) {
    const singleReader = recentReaders[0];
    // Use setTimeout to allow render to complete first
    // Always validate against server to get current public key
    const timeoutId = setTimeout(() => {
      if (onValidate) {
        onValidate(singleReader.id);
      }
    }, 0);
    cleanupFunctions.push(() => clearTimeout(timeoutId));
  }

  // Get input element
  const input = container.querySelector('#reader-id');
  if (!input) {
    return () => {};
  }

  // Track last processed value to avoid duplicate validations
  let lastProcessedValue = readerId;

  function handleInput(e) {
    const value = e.target.value.trim();

    // Check if value matches a recent reader exactly (from datalist selection)
    const matchedReader = recentReaders.find(r => r.id === value);

    if (matchedReader) {
      // User selected from datalist — always validate against server
      // to get the current public key (cached keys may be stale)
      lastProcessedValue = value;
      if (onValidate) {
        onValidate(matchedReader.id);
      }
    } else if (!value) {
      // Input cleared
      lastProcessedValue = '';
      if (onClear) {
        onClear();
      }
    }
    // For partial matches, wait for blur to validate
  }

  function handleBlur(e) {
    const value = e.target.value.trim();

    // Skip if empty, or if same value AND already validated
    // Allow re-validation if previous attempt failed (validated = false)
    if (!value || (value === lastProcessedValue && validated)) {
      return;
    }

    // Validate against server (regardless of whether reader is in recent list)
    lastProcessedValue = value;
    if (onValidate) {
      onValidate(value);
    }
  }

  input.addEventListener('input', handleInput);
  input.addEventListener('blur', handleBlur);

  cleanupFunctions.push(() => {
    input.removeEventListener('input', handleInput);
    input.removeEventListener('blur', handleBlur);
  });

  // Return combined cleanup function
  return function cleanup() {
    cleanupFunctions.forEach((fn) => fn());
  };
}

/**
 * Render loading spinner icon.
 * @returns {string} SVG string
 */
function renderSpinnerIcon() {
  return `
    <svg class="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  `;
}

/**
 * Render green checkmark icon.
 * @returns {string} SVG string
 */
function renderCheckIcon() {
  return `
    <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
    </svg>
  `;
}

/**
 * Escape HTML special characters to prevent XSS.
 * @param {string} str - String to escape
 * @returns {string} Escaped string
 */
function escapeHtml(str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
